import { useEffect, useState } from "react";
import { TFieldValue } from "../types/components/SplitObjectDialog";
import API, { TGetObjectNamesRes } from "../lib/TimeEditAPI";
import { TFieldDefClass, TTypeNameClass, TObjectCreateClass } from "../types/api/APIClassTypes";
import Log from "../lib/Log";
import Language from "../lib/Language";
import { TSelectedCategories } from "../types/models/TSearchSettings";

/** Signature of ObjectSelect's `onClick` selection callback. */
export type TObjectSelectionHandler = (
    object: any,
    activeFilter: TSelectedCategories,
    isModKey: boolean,
    selectedIndexes: number[],
    selectedObjects: { id: number }[]
) => void;

/** First value of the field with `fieldId`, or "" when it is missing. */
const firstValueOf = (values: TFieldValue[], fieldId: number): string =>
    values.find((value) => value.id === fieldId)?.values[0] ?? "";

/**
 * Owns the size split between the existing and the new object. `setNewSize` /
 * `setOldSize` keep the two in sync so they always add up to `initialSize`.
 */
export const useSplitSize = (initialSize: number, remainingCapacity: number) => {
    const [oldObjectSize, setOldObjectSize] = useState<number>(initialSize);
    const [newObjectSize, setNewObjectSize] = useState<number>(0);

    const setNewSize = (val: number | string | null) => {
        if (val === null) return;
        const size = parseInt(val as string, 10);
        setNewObjectSize(size);
        setOldObjectSize((initialSize ?? 0) - size);
    };

    const setOldSize = (val: number | string | null) => {
        if (val === null) return;
        const size = parseInt(val as string, 10);
        setOldObjectSize(size);
        setNewObjectSize((initialSize ?? 0) - size);
    };

    useEffect(() => {
        // If we don't have remainingCapacity at all, or if we have enough of it, split in half.
        if (!remainingCapacity || initialSize < remainingCapacity) {
            setNewSize(initialSize / 2);
        } else {
            // Otherwise it makes sense to cap the existing object at the remaining capacity.
            setOldSize(remainingCapacity);
        }
    }, [initialSize, remainingCapacity]);

    return { oldObjectSize, newObjectSize, setNewSize, setOldSize };
};

/**
 * Owns the field values of the object being split: the unique ("new") fields
 * that must differ from the old object, and the remaining ("other") fields.
 */
export const useFieldValues = (object: TObjectCreateClass, fieldDefs: TFieldDefClass[]) => {
    const [oldFieldValues, setOldFieldValues] = useState<TFieldValue[]>([]);
    const [newFieldValues, setNewFieldValues] = useState<TFieldValue[]>([]);
    const [otherFieldValues, setOtherFieldValues] = useState<TFieldValue[]>([]);
    // Unique fields the user has edited, so a pre-filled value isn't flagged before it is touched.
    const [touchedUniqueIds, setTouchedUniqueIds] = useState<number[]>([]);
    // "Not unique" verdicts from the server, by field id. FieldInput runs those lookups itself;
    // they are mirrored here so they can block the dialog and not just show under the field.
    const [serverUniqueErrors, setServerUniqueErrors] = useState<Record<number, string>>({});

    useEffect(() => {
        const uniqueFieldIds = fieldDefs.filter((f) => f.unique).map((f) => f.id);
        setNewFieldValues(object.fields.filter((f) => uniqueFieldIds.includes(f.id)));
        setOtherFieldValues(object.fields.filter((f) => !uniqueFieldIds.includes(f.id)));
        // Keep the originals around so we can validate that new values actually differ.
        setOldFieldValues([...object.fields]);
    }, [object.fields, fieldDefs]);

    const setNewFieldValue = (fieldId: number, value: string) => {
        setNewFieldValues((prev) => {
            const base = prev.some((f) => f.id === fieldId)
                ? prev
                : [...prev, { id: fieldId, values: [] }];
            return base.map((f) => (f.id === fieldId ? { ...f, values: [value] } : f));
        });
        setTouchedUniqueIds((prev) => (prev.includes(fieldId) ? prev : [...prev, fieldId]));
    };

    /** Record (or, with no message, clear) the server's verdict on a unique field's value. */
    const setUniqueFieldServerError = (fieldId: number, error?: string) => {
        setServerUniqueErrors((prev) => {
            if (prev[fieldId] === error) {
                return prev;
            }
            const next = { ...prev };
            if (error) {
                next[fieldId] = error;
            } else {
                delete next[fieldId];
            }
            return next;
        });
    };

    const updateOtherField = (field: TFieldValue, updateValues: (values: string[]) => void) => {
        const values = ([] as string[]).concat(field.values || []);
        updateValues(values);
        setOtherFieldValues((prev) =>
            prev.map((f) => (f.id === field.id ? { ...field, values } : f))
        );
    };

    const addField = (field: TFieldValue) => {
        updateOtherField(field, (values) => {
            // Zero values are displayed to the user as a single empty value, so go 0 -> 2.
            if (values.length === 0) {
                values.push("");
            }
            values.push("");
        });
    };

    const removeField = (field: TFieldValue, index: number) => {
        updateOtherField(field, (values) => values.splice(index, 1));
    };

    const setFieldValue = (
        field: TFieldValue,
        _event: unknown,
        newValue: string,
        valueIndex: number
    ) => {
        updateOtherField(field, (values) => {
            // eslint-disable-next-line no-param-reassign
            values[valueIndex] = newValue;
        });
    };

    // A unique field must have a value that differs from the object's original value.
    const uniqueFieldError = (fieldId: number): string | undefined => {
        const value = firstValueOf(newFieldValues, fieldId);
        if (!value) {
            return Language.get("nc_split_object_dialog_field_validation_required");
        }
        if (value === firstValueOf(oldFieldValues, fieldId)) {
            return Language.get("nc_split_object_dialog_field_validation_unique");
        }
        return undefined;
    };

    const uniqueFieldsValid = newFieldValues.every(
        (f) => uniqueFieldError(f.id) === undefined && serverUniqueErrors[f.id] === undefined
    );

    // What to show under a field: a backend error wins; otherwise the client error, but only once
    // the field has been touched, so the pre-filled value isn't flagged on load.
    const uniqueFieldDisplayError = (fieldId: number): string | undefined =>
        serverUniqueErrors[fieldId] ??
        (touchedUniqueIds.includes(fieldId) ? uniqueFieldError(fieldId) : undefined);

    return {
        oldFieldValues,
        newFieldValues,
        otherFieldValues,
        setNewFieldValue,
        addField,
        removeField,
        setFieldValue,
        uniqueFieldsValid,
        uniqueFieldDisplayError,
        setUniqueFieldServerError,
    };
};

export const useMemberTransfer = (object: TObjectCreateClass) => {
    const [availableMembers, setAvailableMembers] = useState<TGetObjectNamesRes[]>([]);
    const [selectedMembers, setSelectedMembers] = useState<TGetObjectNamesRes[]>([]);
    const [highlightedAvailableIds, setHighlightedAvailableIds] = useState<number[]>([]);
    const [highlightedSelectedIds, setHighlightedSelectedIds] = useState<number[]>([]);
    const [objectType, setObjectType] = useState<TTypeNameClass>();

    useEffect(() => {
        if (object.members) {
            API.getObjectNames(
                // Members are either plain objects or timed relationships.
                object.members.map((obj) => (obj.object ? obj.object.id : obj.id)),
                false,
                setAvailableMembers
            );
        }
        API.getTypes(object.types, false, (types) => {
            setObjectType(types[0]);
        });
    }, [object]);

    const onAvailableSelectionChanged: TObjectSelectionHandler = (
        _object,
        _filters,
        _isModKey,
        _selectedIndexes,
        selectedObjects
    ) => setHighlightedAvailableIds(selectedObjects.map((obj) => obj.id));

    const onSelectedSelectionChanged: TObjectSelectionHandler = (
        _object,
        _filters,
        _isModKey,
        _selectedIndexes,
        selectedObjects
    ) => setHighlightedSelectedIds(selectedObjects.map((obj) => obj.id));

    const moveToSelected = () => {
        const moved = availableMembers.filter((m) => highlightedAvailableIds.includes(m.id));
        setAvailableMembers((prev) => prev.filter((m) => !highlightedAvailableIds.includes(m.id)));
        setSelectedMembers((prev) => [...moved, ...prev]);
        setHighlightedAvailableIds([]);
        setHighlightedSelectedIds([]);
    };

    const moveAllToSelected = (visibleIds?: number[]) => {
        const isVisible = (member: TGetObjectNamesRes) =>
            !visibleIds || visibleIds.includes(member.id);
        const moved = availableMembers.filter(isVisible);
        setAvailableMembers((prev) => prev.filter((member) => !isVisible(member)));
        setSelectedMembers((prev) => [...moved, ...prev]);
        setHighlightedAvailableIds([]);
        setHighlightedSelectedIds([]);
    };

    const moveToAvailable = () => {
        const moved = selectedMembers.filter((m) => highlightedSelectedIds.includes(m.id));
        setSelectedMembers((prev) => prev.filter((m) => !highlightedSelectedIds.includes(m.id)));
        setAvailableMembers((prev) => [...moved, ...prev]);
        setHighlightedAvailableIds([]);
        setHighlightedSelectedIds([]);
    };

    const moveAllToAvailable = (visibleIds?: number[]) => {
        const isVisible = (member: TGetObjectNamesRes) =>
            !visibleIds || visibleIds.includes(member.id);
        const moved = selectedMembers.filter(isVisible);
        setSelectedMembers((prev) => prev.filter((member) => !isVisible(member)));
        setAvailableMembers((prev) => [...moved, ...prev]);
        setHighlightedAvailableIds([]);
        setHighlightedSelectedIds([]);
    };

    return {
        availableMembers,
        selectedMembers,
        onAvailableSelectionChanged,
        onSelectedSelectionChanged,
        moveToSelected,
        moveAllToSelected,
        moveToAvailable,
        moveAllToAvailable,
        objectType,
    };
};

export const usePartitionMeta = (
    object: TObjectCreateClass,
    templateId: number,
    useSecondSize: boolean
) => {
    const [partitionMemberTypeId, setPartitionMemberTypeId] = useState<number>(0);
    const [partitionFieldIds, setPartitionFieldIds] = useState<number[]>([]);
    const [partitionType, setPartitionType] = useState<TTypeNameClass | null>(null);

    useEffect(() => {
        API.getPartitionMemberType(object.id, templateId, useSecondSize, (status, typeId) => {
            // typeId is 0 if there is no member type.
            if (status.details) {
                Log.warning(status.details);
            }
            if (typeId > 0) {
                API.getTypes([typeId], false, (types) => {
                    setPartitionType(types.length > 0 ? types[0] : null);
                });
            }
            setPartitionMemberTypeId(typeId);
        });
    }, [object, templateId, useSecondSize]);

    useEffect(() => {
        API.getPartitionFields(templateId, useSecondSize, setPartitionFieldIds);
    }, [templateId, useSecondSize]);

    return { partitionMemberTypeId, partitionFieldIds, partitionType };
};

export type TSplitSizeApi = ReturnType<typeof useSplitSize>;
export type TFieldValuesApi = ReturnType<typeof useFieldValues>;
export type TMemberTransferApi = ReturnType<typeof useMemberTransfer>;
